import { OverviewMetricLabel, OverviewMetricValue } from './styledComponents';
import styled, { ThemeContext } from 'styled-components';
import { PlaceholderText } from '../Placeholders';
import TimeSeriesKeyChipColor from '../DataVis/TimeSeriesKeyChipColor';
import { useContext } from 'react';
import useMetricSelect from 'utils/hooks/useMetricSelect';

const Wrapper = styled.div`
    display: grid;
    grid-template-columns: min-content auto;
    grid-template-rows: 1.8rem auto;
`;

const TimeSeriesKeyChipColorCell = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    grid-row: 1 / 2;
`;

const ChipColor = styled(TimeSeriesKeyChipColor)`
    min-height: 0.875rem;
`;

const SelectableOverviewMetricLabel = styled(OverviewMetricLabel)`
    display: flex;
    gap: 0.5rem;
    grid-column: 2 / 3;
    color: ${({ theme }) => theme.gray_700};
    font-size: 0.75rem;
    font-weight: ${({ theme }) => theme.fontBold};
`;

const SelectableOverviewMetricValue = styled(OverviewMetricValue)`
    grid-area: 2 / auto / 1 / auto;
    font-size: 0.875rem;
    font-weight: ${({ theme }) => theme.fontBold};
`;

const getDataSourceColor = (dataSource, theme) => {
    if (dataSource === 'GOOGLE') return theme.google_blue;
    if (dataSource === 'TIKTOK') return theme.tiktok_pink;
    if (dataSource === 'INSTAGRAM') return theme.instagram_orange;
    return null;
};

const DATA_SOURCE_DISPLAY = {
    GOOGLE: 'google',
    TIKTOK: 'TikTok',
    INSTAGRAM: 'instagram',
};

function MetricLabelProtectedCasing({ children }) {
    if (typeof children !== 'string') return children;
    const protectedWordIdx = children.search(/(YoY|QoQ|MoM|WoW)/);
    if (protectedWordIdx < 0) return children;
    return (
        <span>
            {children.slice(0, protectedWordIdx)}
            <span css="text-transform: none;">
                {children.slice(protectedWordIdx, protectedWordIdx + 3)}
            </span>
            {children.slice(protectedWordIdx + 3, children.length)}
        </span>
    );
}

const SelectableOverviewMetric = ({
    metricsList,
    metricsListLabel,
    metrics,
    defaultMetric,
    isLoading,
    tikTokSourceAttributionProps,
    selectable = true,
    showDataSource = true,
    showDataSourceBar = true,
    customCss,
}) => {
    const { selected, formattedValue, selectMenu } = useMetricSelect({
        metricsList,
        metrics,
        defaultMetric,
        tikTokSourceAttributionProps,
    });
    const theme = useContext(ThemeContext);
    const chipColor =
        selected?.key?.replace('_', '') === 'predictedYoY'
            ? theme.purple_1
            : getDataSourceColor(selected?.dataSource, theme);

    return (
        <Wrapper css={customCss}>
            {showDataSourceBar && (
                <TimeSeriesKeyChipColorCell>
                    {chipColor ? <ChipColor $color={chipColor} /> : null}
                </TimeSeriesKeyChipColorCell>
            )}
            <SelectableOverviewMetricValue>
                {!metrics[selected.key] && isLoading ? (
                    <PlaceholderText />
                ) : (
                    formattedValue
                )}
            </SelectableOverviewMetricValue>
            <SelectableOverviewMetricLabel>
                {metricsListLabel
                    ? metricsListLabel
                    : showDataSource
                      ? (DATA_SOURCE_DISPLAY[selected?.dataSource] ??
                        selected?.dataSource?.toLowerCase())
                      : null}
                {selectable ? (
                    selectMenu
                ) : (
                    <MetricLabelProtectedCasing>
                        {selected?.label}
                    </MetricLabelProtectedCasing>
                )}
            </SelectableOverviewMetricLabel>
        </Wrapper>
    );
};

export default SelectableOverviewMetric;
